import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TriageRow } from '../../api/triage.ts';
import { useCommissionOrders } from '../../api/orderRecords.ts';
import { useProperty } from '../../api/properties.ts';
import { triageClose } from '../../tasks/triage.ts';
import { ReportFiling } from '../ReportFiling.tsx';
import { ORDER_DATE, RecordForm } from '../OrderRecords.tsx';
import { RecordVisit as RecordSiteVisit } from '../SiteVisits.tsx';
import { VisitForm } from '../VisitForm.tsx';
import { useClientBonds } from '../../api/bonds.ts';
import { BondForm } from '../BondsPanel.tsx';
import { plain } from './Fields.tsx';

/**
 * Close from triage with the record's own form (P-31; Arthur, 2A as refined, A-#286). A row whose deadline a record
 * completes offers THAT record's form, the very component the client's page draws, posting to the same route, so the
 * permissions, the function and the history are the record's own: a filing (ReportFiling), a welfare visit (VisitForm),
 * a site visit (SiteVisits' form), an order's commission or oath (OrderRecords' form). Saving it completes the deadline
 * by its own path and the row leaves triage on the next read; nothing here writes a deadline. No generic close.
 * A row the reader may not record (ruled Q2 A), or one no record completes, says why in words: release only.
 * Drawn below the row's <label>, never inside it, so nothing here ticks the row.
 */
export function TriageRecord({ row }: { row: TriageRow }) {
  const close = triageClose(row);
  const [open, setOpen] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const qc = useQueryClient();
  if ('why' in close) {
    return <p data-triage-why className="px-3 pb-2 pl-11 text-sm text-neutral-700">{close.why}</p>;
  }
  const done = (message: string) => {
    setOpen(false);
    // said until the row leaves: its deadline is complete, and triage, the held counts and every task list read again
    setSaid(message);
    void qc.invalidateQueries({ queryKey: ['tasks'] });
  };
  return (
    <div data-triage-record={close.kind} className="px-3 pb-2 pl-11">
      {said ? <p role="status" className="text-sm text-neutral-800">{said}</p> : !open ? (
        <button type="button" className={plain} onClick={() => setOpen(true)}>{close.label}</button>
      ) : (
        <RecordFormFor row={row} kind={close.kind} onDone={done} onCancel={() => setOpen(false)} />
      )}
    </div>
  );
}

function RecordFormFor({ row, kind, onDone, onCancel }: {
  row: TriageRow; kind: NonNullable<TriageRow['record_kind']>; onDone: (message: string) => void; onCancel: () => void;
}) {
  switch (kind) {
    case 'filing':
      return (
        <ReportFiling clientId={row.client_id} startOpen
                      row={{ deadline_id: row.deadline_id!, label: row.label, state: 'Not started', filing_id: null,
                             recorded_by: null, recorded_at: null }}
                      onFiled={() => onDone(`Filing recorded: ${row.label}.`)} onCancel={onCancel} />
      );
    case 'visit':
      return (
        <VisitForm clientId={row.client_id} idPrefix={`triage-${row.deadline_id}-`}
                   onRecorded={() => onDone('Visit recorded.')} onCancel={onCancel} />
      );
    case 'site_visit':
      return <SiteVisitFor row={row} onDone={onDone} onCancel={onCancel} />;
    case 'commission':
    case 'oath_designation':
      return <OrderRecordFor row={row} kind={kind} onDone={onDone} onCancel={onCancel} />;
    case 'bond':
      return <BondRecordFor row={row} onDone={onDone} onCancel={onCancel} />;
  }
}

/** 162 (P-35): the order's own Record the bond form, with the order read as the Bonds panel reads it. */
function BondRecordFor({ row, onDone, onCancel }: { row: TriageRow; onDone: (m: string) => void; onCancel: () => void }) {
  const q = useClientBonds(row.client_id);
  if (q.error && !q.data) return <p role="alert" className="text-sm text-critical-edge">The client&rsquo;s bonds could not be read: open the client&rsquo;s Legal section.</p>;
  if (!q.data) return <p className="text-sm text-neutral-700">Loading the order…</p>;
  const order = q.data.orders.find((o) => o.id === row.court_order_id);
  if (!order) {
    return <p className="text-sm text-neutral-700">This order is not among the client&rsquo;s bonded orders: open the client&rsquo;s Legal section.</p>;
  }
  return (
    <BondForm clientId={row.client_id} orderId={order.id} signedDate={order.signed_date}
              riders={q.data.bonds.filter((b) => b.court_order_id === order.id && !b.withdrawn && !b.increases_bond_id)}
              onDone={(m) => (m === null ? onCancel() : onDone(m))} />
  );
}

/** The property's own Record a site visit form, with the property read as on its page (who may visit, its deadline). */
function SiteVisitFor({ row, onDone, onCancel }: { row: TriageRow; onDone: (m: string) => void; onCancel: () => void }) {
  const p = useProperty(row.property_id ?? null);
  if (p.error && !p.data) return <p role="alert" className="text-sm text-critical-edge">The property could not be read: open the client to record the visit.</p>;
  if (!p.data) return <p className="text-sm text-neutral-700">Loading the property…</p>;
  return <RecordSiteVisit detail={p.data} onDone={(m) => (m === null ? onCancel() : onDone(m))} />;
}

/** The order's own commission or oath form, with the order read as the Commission panel reads it. */
function OrderRecordFor({ row, kind, onDone, onCancel }: {
  row: TriageRow; kind: 'commission' | 'oath_designation'; onDone: (m: string) => void; onCancel: () => void;
}) {
  const q = useCommissionOrders(row.client_id);
  if (q.error && !q.data) return <p role="alert" className="text-sm text-critical-edge">The client&rsquo;s orders could not be read: open the client&rsquo;s Legal section.</p>;
  if (!q.data) return <p className="text-sm text-neutral-700">Loading the order…</p>;
  const order = q.data.orders.find((o) => o.id === row.court_order_id);
  if (!order) {
    return <p className="text-sm text-neutral-700">This order is not on the client&rsquo;s Commission panel: open the client&rsquo;s Legal section.</p>;
  }
  return (
    <RecordForm clientId={row.client_id} order={order} kind={kind} initialDate={ORDER_DATE[kind](order) ?? ''}
                onDone={(m) => (m === null ? onCancel() : onDone(m))} />
  );
}
